import React, { useRef, useCallback, useEffect, useState, memo } from "react";
import { FaTimes } from "react-icons/fa";
import SignaturePad from "./SignaturePad";
import { useSignaturePad } from "../hooks/useSignaturePad";
import { useIsMobile } from "../../../hooks/useIsMobile";

const SignatureModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedEmployee,
  cart,
  signaturePadRef: externalRef,
  setSignaturePadRef,
  hasSignature,
  setHasSignature,
  /** 'assignment' | 'transfer' */
  variant = "assignment",
  transferSourceEmployee = null,
  transferTargetEmployee = null,
  /** { item, serial?, quantity }[] — when variant is transfer */
  transferItems = null,
}) => {
  const internalRef = useRef(null);
  const signaturePadRef = externalRef || internalRef;
  const isMobile = useIsMobile();
  const [itemsCollapsed, setItemsCollapsed] = useState(false);

  const isTransfer = variant === "transfer";
  const hasRefund =
    !isTransfer && (cart || []).some((c) => (c.quantity ?? 0) < 0);
  const hasAdd =
    !isTransfer && (cart || []).some((c) => (c.quantity ?? 0) > 0);

  const { clearSignature, getSignatureData } = useSignaturePad(
    signaturePadRef,
    setHasSignature,
    signaturePadRef,
    null,
    setHasSignature,
    null,
  );

  // Update external ref when internal ref changes
  useEffect(() => {
    if (internalRef.current && setSignaturePadRef) {
      setSignaturePadRef(internalRef.current);
    }
  }, [setSignaturePadRef]);

  // Keep items expanded on desktop and collapsed on mobile by default.
  useEffect(() => {
    setItemsCollapsed(isMobile);
  }, [isMobile, isOpen]);

  const handleConfirm = useCallback(async () => {
    if (!hasSignature) {
      alert(
        isTransfer
          ? "נא להוסיף חתימה לפני אישור ההעברה"
          : hasAdd && hasRefund
            ? "נא להוסיף חתימה לפני אישור חתימה וזיכוי"
            : hasRefund
              ? "נא להוסיף חתימה לפני אישור זיכוי"
              : "נא להוסיף חתימה לפני אישור חתימה",
      );
      return;
    }

    const signatureDataUrl = getSignatureData(signaturePadRef);

    if (!signatureDataUrl || !signatureDataUrl.startsWith("data:image")) {
      alert("שגיאה בחתימה. נא לנסות שוב.");
      return;
    }

    await onConfirm(signatureDataUrl);
  }, [
    hasSignature,
    signaturePadRef,
    getSignatureData,
    onConfirm,
    isTransfer,
    hasAdd,
    hasRefund,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
    clearSignature(signaturePadRef);
  }, [onClose, clearSignature, signaturePadRef]);

  if (!isOpen) return null;

  const confirmLabel = isTransfer
    ? "אשר העברה"
    : hasAdd && hasRefund
      ? "אשר חתימה וזיכוי"
      : hasRefund
        ? "אשר זיכוי"
        : hasAdd
          ? "אשר חתימה"
          : "אשר חתימה";

  const modalTitle = isTransfer
    ? "החלפת חתימות — אישור חתימה"
    : hasAdd && hasRefund
      ? "אישור חתימה וזיכוי"
      : hasRefund
        ? "אישור זיכוי"
        : "אישור חתימה";

  const itemsSummaryTitle = isTransfer
    ? "פריטים שיועברו:"
    : hasAdd && hasRefund
      ? "פריטים לאישור חתימה וזיכוי:"
      : hasRefund
        ? "פריטים לאישור זיכוי:"
        : "פריטים לאישור חתימה:";

  const signatureSectionHeading = isTransfer
    ? "חתימה לאישור:"
    : hasAdd && hasRefund
      ? "חתימה לפני אישור חתימה וזיכוי:"
      : hasRefund
        ? "חתימה לפני אישור זיכוי:"
        : "חתימה לפני אישור חתימה:";

  const listItems =
    isTransfer && transferItems?.length
      ? transferItems
      : (cart || []).map((c) => ({
          item: c.item,
          serial: c.serial,
          quantity: c.quantity,
        }));

  return (
    <div className="modal-overlay">
      <div className="modal-content signature-modal">
        <div className="modal-header">
          <h3>{modalTitle}</h3>
          <button className="modal-close" onClick={handleCancel}>
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">
          {isTransfer && transferSourceEmployee && transferTargetEmployee ? (
            <div className="employee-info signature-transfer-confirm-employees">
              <h4>העברת ציוד</h4>
              <p>
                מ־{" "}
                <strong>
                  {transferSourceEmployee.firstName}{" "}
                  {transferSourceEmployee.lastName}
                </strong>{" "}
                (מ.א {transferSourceEmployee.personalNumber})
              </p>
              <p>
                אל{" "}
                <strong>
                  {transferTargetEmployee.firstName}{" "}
                  {transferTargetEmployee.lastName}
                </strong>{" "}
                (מ.א {transferTargetEmployee.personalNumber})
              </p>
            </div>
          ) : (
            <div className="employee-info">
              <h4>
                חייל: {selectedEmployee?.firstName}{" "}
                {selectedEmployee?.lastName}
              </h4>
              <p>מ.א: {selectedEmployee?.personalNumber}</p>
            </div>
          )}

          <div className="items-summary">
            <div
              className="items-summary-header"
              onClick={() => setItemsCollapsed((prev) => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setItemsCollapsed((prev) => !prev);
                }
              }}
            >
              <h4>{itemsSummaryTitle}</h4>
              <span
                className={`details-toggle-arrow ${
                  itemsCollapsed ? "collapsed" : "expanded"
                }`}
              >
                ▼
              </span>
            </div>
            {!itemsCollapsed && (
              <div className="items-list">
                {listItems.map((row, index) => (
                  <div key={index} className="summary-item">
                    <span className="item-name">{row.item}</span>
                    {row.serial && (
                      <span className="item-serial">מס': {row.serial}</span>
                    )}
                    <span className="item-quantity">כמות: {row.quantity}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="signature-section">
            <h4>{signatureSectionHeading}</h4>
            <div className="signature-pad-container">
              <SignaturePad
                canvasRef={signaturePadRef}
                onSignatureChange={setHasSignature}
                signaturePadRef={signaturePadRef}
                setHasSignature={setHasSignature}
                width={400}
                height={200}
              />
              <div className="signature-pad-actions">
                <button
                  className="clear-signature-btn"
                  onClick={() => clearSignature(signaturePadRef)}
                >
                  נקה חתימה
                </button>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleCancel}>
              ביטול
            </button>
            <button
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={!hasSignature}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(SignatureModal);
